<template>
  <ValidationObserver
    ref="observer"
    v-slot="{ valid, dirty }"
  >
    <NavTabs
      customClass="capitalize_title"
      :tabs="tabs"
      :activeTab="newActiveTab"
      @sendData="updateData"
    />

    <SlotScopeWatcher
      :value="dirty"
      @change="$emit('dirtyChanged', dirty)"
    />
    <SlotScopeWatcher
      :value="valid"
      @change="$emit('validChanged', valid)"
    />
    <RedirectModal v-if="showRedirectModal" />
  </ValidationObserver>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import { ValidationObserver } from 'vee-validate';
import SlotScopeWatcher from '@/components/shared/widgets/forms/validationHelper/SlotScopeWatcher';
import NavTabs from '@/components/shared/widgets/Nav-tabs.vue';
import ProgramInfo from '@/components/forms/treatmentProgram/forms/ProgramInfo';
import Function from '@/components/forms/treatmentProgram/forms/Function';
import InjectionMethod from '@/components/forms/treatmentProgram/forms/InjectionMethod';
import TargetDosage from '@/components/forms/treatmentProgram/forms/TargetDosage';
import ProgramControl from '@/components/forms/treatmentProgram/forms/ProgramControl';
import History from '@/components/shared/history.vue';
import lockService from '@/utilities/lockService';
import RedirectModal from '@/components/shared/RedirectModal.vue';

export default {
    name: 'TreatmentProgramForm',
    components: {
        ValidationObserver,
        SlotScopeWatcher,
        NavTabs,
        RedirectModal,
    },
    props: {
        network: {
            type: Object,
            default: null,
        },
        nodeInfo: {
            type: Object,
            default: null,
        },
        isEdition: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            loading: false,
            tabs: [],
            components: [ProgramInfo, Function, InjectionMethod, TargetDosage, ProgramControl],
            empty: {
                programInfo: {
                    siteProduct: '',
                },
                function: {
                    fouling: null,
                    scaling: null,
                    corrosion: null,
                    microbiological: null,
                    other: null,
                },
                injectionMethod: {
                    controleInjection: null,
                    schedule: {
                        cycleType: {
                            type: null,
                            start: null,
                            end: null,
                            difference: null,
                            otherInfo: null,
                        },
                        prebleed: {
                            duration: {
                                value: null,
                                unit: null,
                            },
                            isActive: null,
                        },
                        blowdownLockOut: {
                            duration: {
                                value: null,
                                unit: null,
                            },
                            isActive: null,
                        },
                        schedules: [],
                    },
                    probe: {
                        probeType: null,
                    },
                    manual: {
                        text: null,
                    },
                    pourcentTime: {
                        pourcent: null,
                        period: {
                            value: null,
                            unit: null,
                        },
                    },
                    other: {
                        text: null,
                    },
                },
                targetDosage: {
                    dosage: {
                        value: null,
                        unit: null,
                    },
                    injectionCible: {
                        value: null,
                        unit: null,
                    },
                    modelPump: null,
                    maxPressure: {
                        value: null,
                        unit: null,
                    },
                    maxCapacity: {
                        value: null,
                        unit: null,
                    },
                    realFlow: {
                        value: null,
                        unit: null,
                    },
                    volumeSystem: {
                        value: null,
                        unit: null,
                    },
                    residenceTimeOfSytem: {
                        value: null,
                        unit: null,
                    },
                    halfLife: {
                        value: null,
                        unit: null,
                    },
                    injectionTime: {
                        value: null,
                        unit: null,
                    },
                },
                programControl: {
                    makeUpWaterMeter: null,
                    makeUpWaterLinkToControler: null,
                    blowdownWaterMeter: null,
                    blowdownWaterMeterLinkToControler: null,
                    mark: null,
                    model: null,
                    blowdown: null,
                    conductivity: null,
                    ph: null,
                    temperature: null,
                    orp: null,
                    fluorometer: null,
                    corossionMeter: null,
                    internetConnection: null,
                    other: null,
                    otherInfo: null,
                },
            },
            data: {
                nodeInfo: null,
            },
            model: null,
            actionName: 'treatmentProgram',
            showRedirectModal: false,
        };
    },
    computed: {
        ...mapGetters([
            'activeTab',
            'site',
            'userLang',
        ]),
        newActiveTab: {
            get() {
                return this.activeTab;
            },
            set(newValue) {
                this.$store.dispatch('changeActiveTab', newValue);
            },
        },
    },
    mounted() {
        window.addEventListener('beforeunload', this.handleUnload);
    },

    async created() {
        this.loading = true;
        this.$store.dispatch('changeActiveTab', 0);

        this.model = this.isEdition ? this.nodeInfo : this.empty;
        if (this.model.history && this.model.history.length) {
            // insert in last position
            this.components.splice(this.components.length, 0, History);
        }
        this.tabs = this.tableHeaders();
        this.loading = false;
        if (!this.$store.getters.lockFrom) {
            await this.checkLockOnLoad();
        }
    },
    async beforeRouteLeave(to, from, next) {
        try {
            if (this.nodeInfo && this.nodeInfo.ref) {
                const releaseStatus = await lockService.releaseLock(this.nodeInfo.ref, this.actionName);
                if (!releaseStatus) return next(false);
            }
        } catch (error) {
            console.error('Error releasing lock on route leave:', error);
            // Continue navigation even if lock release fails
        }
        return next();
    },
    beforeDestroy() {
        window.removeEventListener('beforeunload', this.handleUnload);
        // Ensure locks are released even if beforeRouteLeave didn't complete
        if (this.nodeInfo && this.nodeInfo.ref) {
            lockService.releaseLock(this.nodeInfo.ref, this.actionName)
                .catch(err => console.error('Error releasing lock on destroy:', err));
        }
    },
    methods: {
        tableHeaders() {
            const headers = this.components.map((component, index) => ({
                index,
                props: this.model,
                name: this.$t(`treatmentProgram.step${index}.title`),
                component: Vue.component(`tab-${index}`, component),
            }));
            this.$store.dispatch('setTabs', headers);
            return headers;
        },
        handleUnload() {
            if (this.nodeInfo && this.nodeInfo.ref) {
                // Handle synchronously for page unload events
                try {
                    lockService.releaseLock(this.nodeInfo.ref, this.actionName);
                } catch (error) {
                    console.error('Error releasing lock on unload:', error);
                }
            }
        },
        updateData(newData) {
            this.model = { ...this.model, ...newData };
            this.$emit('sendData', this.model);
        },
        async checkLockOnLoad() {
            try {
                if (!this.nodeInfo || !this.nodeInfo.ref) return;
                const lockAvailable = await lockService.getLockStatus(this.nodeInfo.ref, this.actionName);
                if (!lockAvailable) {
                    this.showRedirectModal = true;
                    return;
                }
                await lockService.requestLock(this.nodeInfo.ref, this.actionName);
            } catch (error) {
                console.error('Error checking lock status:', error);
                this.showRedirectModal = true;
            }
        },
    },
};
</script>
